/**
 * Subscription Plan Ranking และ Icon System
 * จัดการการเรียงลำดับและแสดง icon ของ Subscription Plan ต่างๆ
 */

// Subscription Plan Ranking Configuration
export const subscriptionRanking = {
  customer: {
    free: { rank: 1, icon: '🥉', name: 'Free', color: 'bg-gray-100 text-gray-800', description: 'แผนเริ่มต้น' },
    basic: { rank: 2, icon: '🥈', name: 'Basic', color: 'bg-blue-100 text-blue-800', description: 'แผนพื้นฐาน' },
    silver: { rank: 3, icon: '🥇', name: 'Silver', color: 'bg-gray-100 text-gray-800', description: 'แผนเงิน' },
    gold: { rank: 4, icon: '👑', name: 'Gold', color: 'bg-yellow-100 text-yellow-800', description: 'แผนทอง' },
    platinum: { rank: 5, icon: '💎', name: 'Platinum', color: 'bg-purple-100 text-purple-800', description: 'แผนสูงสุด' }
  },
  girl: {
    free: { rank: 1, icon: '🥉', name: 'Free', color: 'bg-gray-100 text-gray-800', description: 'แผนเริ่มต้น' },
    basic: { rank: 2, icon: '🥈', name: 'Basic', color: 'bg-blue-100 text-blue-800', description: 'แผนพื้นฐาน' },
    premium: { rank: 3, icon: '🥇', name: 'Premium', color: 'bg-green-100 text-green-800', description: 'แผนพรีเมียม' },
    vip: { rank: 4, icon: '💎', name: 'VIP', color: 'bg-purple-100 text-purple-800', description: 'แผน VIP' }
  },
  momo: {
    starter: { rank: 1, icon: '🥉', name: 'Starter', color: 'bg-gray-100 text-gray-800', description: 'แผนเริ่มต้น' },
    basic: { rank: 2, icon: '🥈', name: 'Basic', color: 'bg-blue-100 text-blue-800', description: 'แผนพื้นฐาน' },
    pro: { rank: 3, icon: '🥇', name: 'Pro', color: 'bg-green-100 text-green-800', description: 'แผนโปร' },
    enterprise: { rank: 4, icon: '👑', name: 'Enterprise', color: 'bg-purple-100 text-purple-800', description: 'แผนองค์กร' },
    ultraEnterprise: { rank: 5, icon: '💎', name: 'Ultra Enterprise', color: 'bg-red-100 text-red-800', description: 'แผนองค์กรขั้นสูง' }
  }
};

/**
 * ดึงข้อมูล ranking ของ subscription plan
 * @param {string} role - role ของผู้ใช้ (customer, girl, momo)
 * @param {string} subscriptionKey - key ของ subscription plan
 * @returns {object|null} - ข้อมูล ranking หรือ null
 */
export const getSubscriptionRanking = (role, subscriptionKey) => {
  if (!subscriptionRanking[role] || !subscriptionRanking[role][subscriptionKey]) {
    return null;
  }
  return subscriptionRanking[role][subscriptionKey];
};

/**
 * เรียง subscription configurations ตาม rank
 * @param {Array} subscriptionConfigs - รายการ subscription configurations
 * @param {string} role - role ของผู้ใช้
 * @returns {Array} - subscription configurations ที่เรียงตาม rank
 */
export const getSortedSubscriptionsByRank = (subscriptionConfigs, role) => {
  return subscriptionConfigs.sort((a, b) => {
    const aRank = getSubscriptionRanking(role, a.key)?.rank || 999;
    const bRank = getSubscriptionRanking(role, b.key)?.rank || 999;
    return aRank - bRank;
  });
};

/**
 * สร้าง options สำหรับ dropdown
 * @param {Array} subscriptionConfigs - รายการ subscription configurations
 * @param {string} role - role ของผู้ใช้
 * @returns {Array} - options สำหรับ dropdown
 */
export const getSubscriptionDropdownOptions = (subscriptionConfigs, role) => {
  return subscriptionConfigs.map(config => {
    const ranking = getSubscriptionRanking(role, config.key);
    return {
      value: config.key,
      label: ranking ? `${ranking.icon} ${ranking.name} (Rank ${ranking.rank})` : config.name,
      ranking: ranking
    };
  });
};

/**
 * สร้าง subscription badge object
 * @param {string} role - role ของผู้ใช้
 * @param {string} subscriptionKey - key ของ subscription plan
 * @param {object} subscriptionData - ข้อมูล subscription plan
 * @param {string} size - ขนาดของ badge (sm, md, lg)
 * @returns {object} - subscription badge object
 */
export const createSubscriptionBadge = (role, subscriptionKey, subscriptionData, size = 'md') => {
  const ranking = getSubscriptionRanking(role, subscriptionKey);
  if (!ranking) return null;

  const sizeClasses = {
    sm: 'text-xs px-2 py-1',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-2'
  };

  return {
    icon: ranking.icon,
    name: ranking.name,
    rank: ranking.rank,
    color: ranking.color,
    description: ranking.description,
    sizeClass: sizeClasses[size] || sizeClasses.md,
    fullText: `${ranking.icon} ${ranking.name}`
  };
};

/**
 * ตรวจสอบว่า subscription plan หนึ่งสูงกว่าอีกอันหรือไม่
 * @param {string} role - role ของผู้ใช้
 * @param {string} subscription1 - subscription plan แรก
 * @param {string} subscription2 - subscription plan ที่สอง
 * @returns {boolean} - true ถ้า subscription1 สูงกว่า subscription2
 */
export const isSubscriptionHigher = (role, subscription1, subscription2) => {
  const rank1 = getSubscriptionRanking(role, subscription1)?.rank || 0;
  const rank2 = getSubscriptionRanking(role, subscription2)?.rank || 0;
  return rank1 > rank2;
};

/**
 * ดึง subscription plans ที่สูงกว่าปัจจุบัน
 * @param {string} role - role ของผู้ใช้
 * @param {string} currentSubscription - subscription plan ปัจจุบัน
 * @param {Array} subscriptionConfigs - รายการ subscription configurations
 * @returns {Array} - subscription plans ที่สูงกว่า
 */
export const getHigherSubscriptions = (role, currentSubscription, subscriptionConfigs) => {
  const currentRank = getSubscriptionRanking(role, currentSubscription)?.rank || 0;
  return subscriptionConfigs.filter(config => {
    const configRank = getSubscriptionRanking(role, config.key)?.rank || 0;
    return configRank > currentRank;
  });
};

/**
 * สร้าง progress bar สำหรับ subscription upgrade
 * @param {string} role - role ของผู้ใช้
 * @param {string} currentSubscription - subscription plan ปัจจุบัน
 * @returns {object} - progress data
 */
export const createSubscriptionProgress = (role, currentSubscription) => {
  const currentRank = getSubscriptionRanking(role, currentSubscription)?.rank || 1;
  const maxRank = Math.max(...Object.values(subscriptionRanking[role] || {}).map(s => s.rank));
  const progress = (currentRank / maxRank) * 100;

  return {
    currentRank,
    maxRank,
    progress,
    percentage: Math.round(progress)
  };
};

// Export สำหรับ backward compatibility
export const tierRanking = subscriptionRanking;
export const getTierRanking = getSubscriptionRanking;
export const getSortedTiersByRank = getSortedSubscriptionsByRank;
export const getTierDropdownOptions = getSubscriptionDropdownOptions;
export const createTierBadge = createSubscriptionBadge;
export const isTierHigher = isSubscriptionHigher;
export const getHigherTiers = getHigherSubscriptions;
export const createTierProgress = createSubscriptionProgress;









